import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getModelFams,
  getCloudCatalogModels,
  registerOllamaChatModel,
} from '../services/api';

/** Shared across picker instances to avoid duplicate slow Ollama/catalog fetches. */
const ollamaFamiliesCache = { data: null, promise: null };
const cloudModelsCache = new Map();

function loadOllamaFamilies(keycloak) {
  if (ollamaFamiliesCache.data) {
    return Promise.resolve(ollamaFamiliesCache.data);
  }
  if (!ollamaFamiliesCache.promise) {
    ollamaFamiliesCache.promise = getModelFams(keycloak)
      .then((fams) => {
        ollamaFamiliesCache.data = Array.isArray(fams) ? fams : [];
        return ollamaFamiliesCache.data;
      })
      .catch(() => {
        ollamaFamiliesCache.data = [];
        return ollamaFamiliesCache.data;
      })
      .finally(() => {
        ollamaFamiliesCache.promise = null;
      });
  }
  return ollamaFamiliesCache.promise;
}

function cloudCacheKey(providerKey, search, catalogType) {
  return `${providerKey}|${catalogType}|${search || ''}`;
}

async function loadCloudModels(keycloak, providerKey, search, catalogType) {
  const key = cloudCacheKey(providerKey, search, catalogType);
  if (cloudModelsCache.has(key)) {
    return cloudModelsCache.get(key);
  }
  const models = await getCloudCatalogModels(keycloak, providerKey, search || '', catalogType);
  const list = Array.isArray(models) ? models : [];
  cloudModelsCache.set(key, list);
  return list;
}

function clearPickerDataCaches() {
  ollamaFamiliesCache.data = null;
  ollamaFamiliesCache.promise = null;
  cloudModelsCache.clear();
}

function initialFromRecord(record) {
  if (!record) {
    return { source: 'local', providerId: null, model: '' };
  }
  let isLocal;
  if (record.source !== undefined) {
    isLocal = record.source === 'local';
  } else {
    isLocal = record.is_local !== false;
  }
  return {
    source: isLocal ? 'local' : 'cloud',
    providerId: record.cloud_provider_id ?? null,
    model: record.model || '',
  };
}

/**
 * Vault-style local/cloud LLM model picker state + fetch effects.
 */
export default function useLlmModelPicker({
  keycloak,
  enabled = true,
  cloudProviders = [],
  initial = null,
  catalogType = 'chat',
}) {
  const init = initialFromRecord(initial);
  const [source, setSourceState] = useState(init.source);
  const [providerId, setProviderIdState] = useState(init.providerId);
  const [model, setModel] = useState(init.model);
  const [cloudSearch, setCloudSearch] = useState('');
  const [cloudModels, setCloudModels] = useState([]);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [ollamaFamilies, setOllamaFamilies] = useState([]);
  const [ollamaLoading, setOllamaLoading] = useState(false);
  const cloudSearchDebounceRef = useRef(null);
  const [debouncedCloudSearch, setDebouncedCloudSearch] = useState('');

  useEffect(() => {
    if (cloudSearchDebounceRef.current) clearTimeout(cloudSearchDebounceRef.current);
    cloudSearchDebounceRef.current = setTimeout(() => {
      setDebouncedCloudSearch(cloudSearch);
    }, 300);
    return () => {
      if (cloudSearchDebounceRef.current) clearTimeout(cloudSearchDebounceRef.current);
    };
  }, [cloudSearch]);

  const setSource = useCallback((v) => {
    setSourceState(v);
    setModel('');
    setProviderIdState(null);
    setCloudSearch('');
  }, []);

  const setProviderId = useCallback((id) => {
    setProviderIdState(id);
    setModel('');
  }, []);

  const hydrateFrom = useCallback((record) => {
    const next = initialFromRecord(record);
    setSourceState(next.source);
    setProviderIdState(next.providerId);
    setModel(next.model);
    setCloudSearch('');
  }, []);

  const reset = useCallback(() => {
    hydrateFrom(null);
    setCloudModels([]);
    setOllamaFamilies([]);
  }, [hydrateFrom]);

  const registerOllama = useCallback(
    async (name) => {
      await registerOllamaChatModel(keycloak, name);
      clearPickerDataCaches();
      const fams = await loadOllamaFamilies(keycloak);
      setOllamaFamilies(fams);
    },
    [keycloak],
  );

  const toFields = useCallback(
    () => ({
      is_local: source === 'local',
      model: (model || '').trim() || null,
      cloud_provider_id: source === 'cloud' ? providerId : null,
    }),
    [source, model, providerId],
  );

  const toWidgetFields = useCallback(
    (prefix) => {
      const fields = toFields();
      return {
        [`${prefix}_is_local`]: fields.is_local,
        [`${prefix}_model`]: fields.model,
        [`${prefix}_cloud_provider_id`]: fields.cloud_provider_id,
      };
    },
    [toFields],
  );

  const validateCloud = useCallback(
    (label = 'cloud model') => {
      if (source !== 'cloud') return true;
      if (!providerId || !(model || '').trim()) {
        return `For ${label}, select a Vault provider and a model, or switch to Local.`;
      }
      return true;
    },
    [source, providerId, model],
  );

  const toBotQuestionLlm = useCallback(
    () => ({
      source,
      model: model || '',
      cloud_provider_id: providerId,
    }),
    [source, model, providerId],
  );

  // Ollama families when local
  useEffect(() => {
    if (!enabled || !keycloak?.authenticated || source !== 'local') return;
    let cancelled = false;
    if (ollamaFamiliesCache.data) {
      setOllamaFamilies(ollamaFamiliesCache.data);
      setOllamaLoading(false);
      return undefined;
    }
    setOllamaLoading(true);
    loadOllamaFamilies(keycloak)
      .then((fams) => {
        if (!cancelled) setOllamaFamilies(fams);
      })
      .catch(() => {
        if (!cancelled) setOllamaFamilies([]);
      })
      .finally(() => {
        if (!cancelled) setOllamaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, keycloak, source]);

  // Cloud catalog when cloud + provider selected
  useEffect(() => {
    if (!enabled || !keycloak?.authenticated || source !== 'cloud' || !providerId) {
      if (source === 'cloud' && !providerId) {
        setCloudModels([]);
        setCloudLoading(false);
      }
      return undefined;
    }
    const prov = cloudProviders.find((p) => p.id === providerId);
    if (!prov?.provider) {
      setCloudModels([]);
      return undefined;
    }
    const cacheKey = cloudCacheKey(prov.provider, debouncedCloudSearch, catalogType);
    if (cloudModelsCache.has(cacheKey)) {
      setCloudModels(cloudModelsCache.get(cacheKey));
      setCloudLoading(false);
      return undefined;
    }
    let cancelled = false;
    setCloudLoading(true);
    loadCloudModels(keycloak, prov.provider, debouncedCloudSearch, catalogType)
      .then((models) => {
        if (!cancelled) setCloudModels(models);
      })
      .catch(() => {
        if (!cancelled) setCloudModels([]);
      })
      .finally(() => {
        if (!cancelled) setCloudLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, keycloak, source, providerId, debouncedCloudSearch, cloudProviders, catalogType]);

  return {
    source,
    providerId,
    model,
    cloudSearch,
    cloudModels,
    cloudLoading,
    ollamaFamilies,
    ollamaLoading,
    setSource,
    setProviderId,
    setModel,
    setCloudSearch,
    hydrateFrom,
    reset,
    registerOllama,
    toFields,
    toWidgetFields,
    toBotQuestionLlm,
    validateCloud,
  };
}
